import './AppRatingsBlaze.scss';

import { Body, ButtonV2, Icon, IconName, useConfig } from '@travelwin/core';
import { FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalytics } from '@/analytics';
import { Link } from '@/components/Link';
import { useCreateStoreLink } from '@/components/MobileStoreButton/useCreateStoreLink';
import { useDeviceOS } from '@/hooks/useDeviceOS';

interface Props {
  appRatings?: {
    android?: number;
    iOS?: number;
  };
}

const baseClassName = 'c-AppRatingsBlaze';

export const AppRatingsBlaze: FC<Props> = ({ appRatings }) => {
  const analytics = useAnalytics();
  const { isMobileWeb } = useDeviceOS();
  const { APPLE_STORE_APP_URL, GOOGLE_PLAY_APP_URL } = useConfig();
  const { t } = useTranslation();

  if (!appRatings) return null;

  const AndroidRating = appRatings.android ? (
    <AppRating
      iconName="googlePlayIcon"
      rating={appRatings.android}
      availableOn={
        <Body>{t('app_ratings.google_play', 'on Google Play')}</Body>
      }
      directStoreLink={isMobileWeb ? GOOGLE_PLAY_APP_URL : undefined}
      onClick={() => {
        analytics?.trackClickAction({
          element_id: 'btn_play_store',
          element_text: `${appRatings.android} on Google Play`,
        });
      }}
    />
  ) : null;

  const iOSRating = appRatings.iOS ? (
    <AppRating
      iconName="appStoreIcon"
      rating={appRatings.iOS}
      availableOn={<Body>{t('app_ratings.app_store', 'on App Store')}</Body>}
      directStoreLink={isMobileWeb ? APPLE_STORE_APP_URL : undefined}
      onClick={() => {
        analytics?.trackClickAction({
          element_id: 'btn_app_store',
          element_text: `${appRatings.android} on App Store`,
        });
      }}
    />
  ) : null;

  return (
    <div className={baseClassName}>
      {AndroidRating}
      {iOSRating}
    </div>
  );
};

const AppRating = ({
  iconName,
  rating,
  availableOn,
  directStoreLink,
  onClick,
}: {
  iconName: Extract<IconName, 'appStoreIcon' | 'googlePlayIcon'>;
  rating: number;
  availableOn: ReactElement;
  directStoreLink?: string;
  onClick: () => void;
}) => {
  const storeLink = useCreateStoreLink();
  const buttonLink = directStoreLink ?? storeLink;

  if (!buttonLink) return null;
  return (
    <ButtonV2
      asChild
      className={`${baseClassName}__rating`}
      variant="tertiary-gray"
    >
      <Link
        to={buttonLink}
        target="_blank"
        className={`${baseClassName}__rating-link`}
        onClick={onClick}
      >
        <Icon name={iconName} />
        <Body weight="600">{rating}</Body>
        <div>
          <Icon name="starIcon" />
        </div>
        {availableOn}
      </Link>
    </ButtonV2>
  );
};
